<template>
  <div class="flex flex-col items-center">
    <!-- Field Label -->
    <label :for="item.field" class="block relative text-sm font-medium uppercase text-primary-800 mb-4">
      <span v-if="item.required" class="inline mr-1 text-sm text-red-400">*</span>
      {{ item.name }}
    </label>

    <!-- File Display -->
    <div class="w-full max-w-md bg-white rounded-md shadow-sm p-4 mb-4 flex flex-col items-center">
      <!-- Upload Progress -->
      <div v-if="sending" class="w-full max-w-md mt-4 text-center">
        <TgLoader />
        <div class="mt-2 text-gray-600">
          Uploading {{ complete }} of {{ total }} files...
        </div>
      </div>
      <template v-else>
        <template v-if="props.modelValue">
          <!-- File Preview Based on Type -->
          <div class="flex flex-col items-center">
            <!-- Image Preview -->
            <template v-if="isImageFile && !imgError">
              <img class="mx-auto max-w-full max-h-48 rounded" :src="fileUrl" @error="handleImgError">
            </template>

            <!-- PDF Icon -->
            <template v-else-if="isPdfFile">
              <svg
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                stroke="currentColor" class="w-16 h-16 text-red-600"
              >
                <path
                  stroke-linecap="round" stroke-linejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                />
              </svg>
              <div class="text-sm text-gray-600 mt-2">
                PDF File
              </div>
            </template>

            <!-- Generic File Icon -->
            <template v-else>
              <svg
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                stroke="currentColor" class="w-16 h-16 text-gray-600"
              >
                <path
                  stroke-linecap="round" stroke-linejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 18H8.25c-.621 0-1.125-.504-1.125-1.125V1.875c0-.621.504-1.125 1.125-1.125h3m5.25 0v17.25c0 .621.504 1.125 1.125 1.125h1.5c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                />
              </svg>
              <div class="text-sm text-gray-600 mt-2">
                {{ fileExtension.toUpperCase() }} File
              </div>
            </template>

            <!-- File Name -->
            <div class="text-xs text-gray-500 mt-1 text-center break-all">
              {{ fileName }}
            </div>
          </div>

          <div class="mt-4 flex space-x-4">
            <button type="button" class="text-sm text-red-400" @click="del">
              Remove
            </button>
            <button type="button" class="text-sm text-primary-600" @click="triggerFilePickerAndReset">
              Replace
            </button>
            <button type="button" class="text-sm text-blue-600 hover:text-blue-800" @click="getSecureDownloadUrl">
              View/Download
            </button>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col items-center cursor-pointer" @click="triggerFilePickerAndReset">
            <svg
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
              stroke="currentColor" class="w-12 h-12 text-gray-400"
            >
              <path
                stroke-linecap="round" stroke-linejoin="round"
                d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18.75 19.5H6.75Z"
              />
            </svg>
            <div class="mt-2 text-gray-600">
              Click to upload a file
            </div>
            <div class="mt-1 text-xs text-gray-500">
              Supports PDF, JPG, JPEG, PNG files
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- Hidden File Input for Uploader -->
    <input
      id="file-upload" ref="fileRef" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" class="hidden"
      @change="onChange"
    >
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import TgLoader from '@/components/Common/TgLoader.vue'
import _head from 'lodash/head'
import { API } from '@/api'

const props = defineProps({
  item: Object,
  filename: String,
  folder: String,
  slug: String,
  error: String,
  modelValue: {
    type: [String, Number, Boolean],
    default: '',
  }
})
const emit = defineEmits(['update:modelValue', 'update:error'])

const imgError = ref(false)

const fileUrl = computed(() => {
  if (!props.modelValue) return ''
  return props.modelValue.startsWith('http')
    ? props.modelValue
    : `https://assets.touchpointglobal.com/${props.modelValue}`
})

const fileName = computed(() => {
  if (!props.modelValue) return ''
  const url = fileUrl.value
  return url.split('/').pop() || 'Unknown file'
})

const fileExtension = computed(() => {
  const name = fileName.value
  const parts = name.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
})

const isImageFile = computed(() => {
  const ext = fileExtension.value
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)
})

const isPdfFile = computed(() => {
  return fileExtension.value === 'pdf'
})


const route = useRoute()
const clientKey = computed(() => route.params.client)

const fileRef = ref(null)
const previewFiles = ref([])
const complete = ref(0)
const total = ref(0)
const sending = ref(false)

function onChange(event) {
  const files = event.target.files
  if (files.length) {
    const fileArray = Array.from(files)
    previewFiles.value = fileArray
    total.value = fileArray.length
    complete.value = 0
    sending.value = true

    Promise.all(fileArray.map((file, index) => get_secure_upload_url(file, index)))
      .then(() => {
        sending.value = false
        if (fileRef.value) fileRef.value.value = ''
        previewFiles.value = []
      })
      .catch(err => {
        console.error('Error uploading files:', err)
        sending.value = false
      })
  }
}

async function get_secure_upload_url(file, index) {
  previewFiles.value[index] = {
    name: file.name,
    type: file.type,
    status: "Getting secure URL"
  }

  // Get current date for path structure
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')

  // Build the new path structure: {apikey}/TTL=99-Years/{folder}/{yyyy}/{mm}/{dd}/{filename}
  const filekey = `${API.apikey}/TTL=99-Years/${props.folder}/${yyyy}/${mm}/${dd}/${file.name}`

  const params = {
    action: "put",
    folder: props.folder,
    filekey: filekey,
    bucket: 'workers.tplive.au',
    pub: 0,
    filetype: file.type,
  }

  try {
    const resp = await API.get_secure_upload_url(props.slug, file.name, params)

    const { url, key, link } = resp.data.data

    await upload(file, url)

    // Build the final public URL using the new format
    const publicUrl = `https://s3.ap-southeast-2.amazonaws.com/workers.tplive.au/${filekey}`

    console.log("✅ Uploaded:", publicUrl)
    imgError.value = false;
    emit("update:modelValue", publicUrl)
    complete.value++
  } catch (err) {
    console.error("Upload failed:", err)
    previewFiles.value[index].status = "Failed"
    emit("update:error", 'Failed to upload')
  }
}

async function upload(file, signedUrl) {
  return await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  })
}

function del() {
  emit('update:modelValue', '')
}

function handleImgError() {
  imgError.value = true
}

function triggerFilePicker() {
  if (fileRef.value) {
    fileRef.value.click()
  }
}

function triggerFilePickerAndReset() {
  imgError.value = false
  triggerFilePicker()
}

async function getSecureDownloadUrl() {
  if (!props.modelValue) return

  try {
    sending.value = true
    const resp = await API.get_secure_download_url(props.modelValue)

    if (resp && resp.data && resp.data.data) {
      const secureUrl = resp.data.data
      const win = window.open(secureUrl, '_blank')
      if (win) win.focus()
    }
  } catch (err) {
    console.error('Error getting secure download URL:', err)
    emit('update:error', 'Failed to get secure download link')
  } finally {
    sending.value = false
  }
}
</script>



<style scoped>
.uploader-container {
  max-width: 500px;
  margin: 0 auto;
}

.file-upload-label {
  font-size: 1rem;
  color: #4a5568;
}

.preview-area {
  font-size: 0.9rem;
}

.preview-item {
  background-color: #f7fafc;
}
</style>